import type { ConversationRuntimeSessionMessage } from '@/IsekaiRPG/modules/conversation/conversationTypes';
import type { GameEventEntry } from '@/IsekaiRPG/modules/event/eventTypes';
import type { useStoryHistoryStore } from './historyStores';
import type { StoryHistoryEntry } from './historyTypes';

type StoryHistoryStore = ReturnType<typeof useStoryHistoryStore>;

export interface StoryHistoryConversationContext {
  sessionKey: string;
  sceneName?: string;
  participantIds?: string[];
  sourceId?: string;
  metadata?: Record<string, unknown>;
}

function mergeMetadata(
  base?: Record<string, unknown>,
  extra?: Record<string, unknown>,
): Record<string, unknown> | undefined {
  if (!base && !extra) return undefined;
  return {
    ...(base || {}),
    ...(extra || {}),
  };
}

export class StoryHistoryService {
  static fromGameEventEntry(event: GameEventEntry): StoryHistoryEntry {
    const location = event.metadata?.eventContext as
      | Partial<{
          location: {
            zoneName?: string;
          };
        }>
      | undefined;

    return {
      id: event.id,
      timestamp: event.timestamp,
      gameTime: event.gameTime,
      role: 'system',
      sourceType:
        event.type === 'combat'
          ? 'combat'
          : event.type === 'exploration'
            ? 'exploration'
            : event.type === 'quest'
              ? 'quest'
              : 'event',
      sourceId: event.id,
      sceneName: location?.location?.zoneName,
      participantIds: [],
      content: event.content,
      metadata: event.metadata,
    };
  }

  static fromConversationMessage(
    message: ConversationRuntimeSessionMessage,
    context: StoryHistoryConversationContext,
  ): StoryHistoryEntry {
    return {
      id: message.id,
      timestamp: message.createdAt,
      gameTime: message.gameTime,
      role: message.role,
      sourceType: 'conversation',
      sourceId: context.sourceId ?? context.sessionKey,
      sessionKey: context.sessionKey,
      sceneName: context.sceneName,
      participantIds: [...(context.participantIds ?? [])],
      content: message.content,
      metadata: mergeMetadata(message.metadata, context.metadata),
    };
  }

  static appendGameEvent(store: StoryHistoryStore, event: GameEventEntry): StoryHistoryEntry {
    return store.appendEntry(this.fromGameEventEntry(event));
  }

  static appendConversationMessage(
    store: StoryHistoryStore,
    message: ConversationRuntimeSessionMessage,
    context: StoryHistoryConversationContext,
  ): StoryHistoryEntry {
    return store.appendEntry(this.fromConversationMessage(message, context));
  }

  static syncConversationMessage(
    store: StoryHistoryStore,
    message: ConversationRuntimeSessionMessage,
    context: StoryHistoryConversationContext,
  ): StoryHistoryEntry {
    const nextEntry = this.fromConversationMessage(message, context);
    const existingEntry = store.getEntry(nextEntry.id);

    if (!existingEntry) {
      return store.appendEntry(nextEntry);
    }

    store.updateEntry(
      nextEntry.id,
      {
        role: nextEntry.role,
        sourceType: nextEntry.sourceType,
        sourceId: nextEntry.sourceId,
        sessionKey: nextEntry.sessionKey,
        participantIds: [...nextEntry.participantIds],
        gameTime: nextEntry.gameTime,
        content: nextEntry.content,
        metadata: nextEntry.metadata,
      },
      false,
    );

    return store.getEntry(nextEntry.id) ?? nextEntry;
  }
}
